import type { SharingOption } from '~/components/ShareContent';
import type { Property, ShortVideoProperty } from '~/core/entities/property';
import type { SharePropertyClicked } from '~/types/mixpanel';
import type { MaybeRefOrGetter } from 'vue';

import { onClickOutside, useClipboard } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { concatUrlPath, hasShortsVideo } from '~/utils';
import { toValue } from 'vue';

import { CopyToastDescription } from '~/components/CopyToastDescription';
import { useToast } from '~/components/ui/toast';

interface UsePropertyHeroShareOptions {
	property: Ref<Property | ShortVideoProperty | null>;
	/** When set, overrides the URL built from the current route (e.g. shorts deep link). */
	shareUrlOverride?: MaybeRefOrGetter<string | undefined>;
	/** Optional body text for `navigator.share` (native share sheet). */
	shareTextOverride?: MaybeRefOrGetter<string | undefined>;
	/** When set, overrides the `navigator.share` title (defaults to the property ad title). */
	shareTitleOverride?: MaybeRefOrGetter<string | undefined>;
	/** Called after copying the link when native share is unavailable or unsupported. */
	onAfterFallbackCopy?: () => void;
	/** Merged into every `share_property_clicked` payload (e.g. Shorts analytics context). */
	getShareTrackExtras?: MaybeRefOrGetter<
		Partial<SharePropertyClicked> | undefined
	>;
}

export function usePropertyHeroShare({
	property,
	shareUrlOverride,
	shareTextOverride,
	shareTitleOverride,
	onAfterFallbackCopy,
	getShareTrackExtras
}: UsePropertyHeroShareOptions) {
	const { ORIGIN } = useRuntimeConfig().public;
	const router = useRouter();
	const { copy } = useClipboard();
	const { toast } = useToast();
	const { trackSharePropertyClicked } = useEvents();

	function mergeShareTrackPayload(
		base: Partial<SharePropertyClicked>
	): Partial<SharePropertyClicked> {
		const extras = getShareTrackExtras
			? toValue(getShareTrackExtras)
			: undefined;
		const propertyVideoShorts =
			property.value == null
				? undefined
				: { [MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(property.value) };
		return {
			...(propertyVideoShorts ?? {}),
			...(extras ?? {}),
			...base
		};
	}

	const showShareContent = ref(false);
	const shareContentRef = ref<HTMLElement | null>(null);
	const shareContentButton = ref<HTMLElement | null>(null);

	const shareUrl = computed<string>(() => {
		const override = shareUrlOverride ? toValue(shareUrlOverride) : undefined;
		if (override) return override;
		return concatUrlPath(ORIGIN, router.currentRoute.value.fullPath);
	});

	onClickOutside(shareContentRef, () => (showShareContent.value = false), {
		ignore: [shareContentButton]
	});

	function toggleShareContent() {
		showShareContent.value = !showShareContent.value;
	}

	function resolveNativeShareData(): ShareData | null {
		if (!navigator?.share) return null;

		const url = shareUrl.value;
		const titleOverride = shareTitleOverride
			? toValue(shareTitleOverride)
			: undefined;
		const title =
			titleOverride || property.value?.ad?.title || 'Vídeo do imóvel';
		const text = shareTextOverride ? toValue(shareTextOverride) : undefined;

		const dataWithText: ShareData = {
			title,
			url,
			...(text ? { text } : {})
		};
		if (navigator.canShare?.(dataWithText)) return dataWithText;

		const dataMinimal: ShareData = { title, url };
		if (navigator.canShare?.(dataMinimal)) return dataMinimal;

		return null;
	}

	function getMobileShareChannel(): 'native_share' | 'copy_link' {
		return resolveNativeShareData() ? 'native_share' : 'copy_link';
	}

	async function shareMobile() {
		const url = shareUrl.value;
		const nativeShareData = resolveNativeShareData();

		if (nativeShareData) {
			try {
				await navigator.share(nativeShareData);
				trackSharePropertyClicked(
					mergeShareTrackPayload({
						share_whatsapp: false,
						share_facebook: false,
						copy_link: false,
						share_email: false,
						share_twitter: false,
						share_instagram: false,
						share_clicked: true
					})
				);
			} catch {
				// User cancelled or share failed — do not fall through to copy
			}
			return;
		}

		await copy(url);
		trackSharePropertyClicked(
			mergeShareTrackPayload({
				copy_link: true,
				share_whatsapp: false,
				share_facebook: false,
				share_email: false,
				share_twitter: false,
				share_instagram: false
			})
		);
		toast({
			description: h(CopyToastDescription)
		});
		onAfterFallbackCopy?.();
	}

	function handleShareClicked(option: SharingOption) {
		trackSharePropertyClicked(
			mergeShareTrackPayload({
				share_whatsapp: option.label === 'WhatsApp',
				share_facebook: option.label === 'Facebook',
				copy_link: false,
				share_email: option.label === 'E-mail' || option.label === 'Via e-mail',
				share_twitter: option.label === 'Twitter',
				share_instagram: option.label === 'Instagram',
				share_clicked: true
			})
		);
	}

	return {
		showShareContent,
		shareContentRef,
		shareContentButton,
		shareUrl,
		toggleShareContent,
		shareMobile,
		getMobileShareChannel,
		handleShareClicked
	};
}
